import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCellValue } from '../utils/formatters';
import KVSLoader from '../../../components/loaders/KVSLoader';
import { apiGet } from '../../../shared/services/apiClient';

interface SchoolPopupProps {
  schoolID: number;
  schoolName: string;
  basicInfo?: {
    roName?: string;
    stateName?: string;
    districtName?: string;
    isPMShree?: number;
    latitude?: number;
    longitude?: number;
  };
  selectedKPI?: {
    category?: string;
    kpiField?: string;
    kpiValue?: any;
    kpiLabel?: string;
  };
  schoolKPIData?: any; // The full row data from KPI table
  onViewFullDetails: () => void;
  onClose: () => void;
}

interface ContactDetail {
  id: number;
  role: number;
  roleName: string;
  name: string;
  contactNo: string;
  emailID: string;
}

interface SchoolData {
  schoolID: number;
  uuid?: string;
  schoolName: string;
  roID: number;
  roName: string;
  contactNo: string;
  emailID: string;
  website: string;
  stationName: string;
  stationType: string;
  stateID: number;
  stateName?: string;
  districtID: number;
  districtName?: string;
  city: string;
  postalAddress1: string;
  postalAddress2: string;
  postalAddress3: string;
  pinCode: number;
  constituencyID: number;
  constituencyName?: string;
  longitude: string;
  latitude: string;
  locationType: number;
  establishmentYear: string;
  udiseCode: string;
  cbseAffliation: string;
  cbseSchoolCode: string;
  sponsoredBy: number;
  sponsoringAgency: string;
  sponsoringAgencyQuota: number;
  uptoClass?: number;
  sections?: number;
  schoolBuilding?: number;
  infraFacility?: number;
  isPMShree: number;
  status: number;
  principalName: string;
  principalContactNo: string;
  contactDetails?: ContactDetail[];
  createdAt?: string;
  updatedAt?: string;
  ownershipType: number;
}

const SchoolPopup: React.FC<SchoolPopupProps> = ({
  schoolID,
  schoolName,
  basicInfo,
  onClose,
  selectedKPI,
  schoolKPIData,
}) => {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState<SchoolData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleViewAll = () => {
    const url = `${window.location.origin}/kvs-samagam/school/${schoolID}`;
    // open full details in a new tab (use noopener for security)
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    const fetchSchoolData = async () => {
      try {
        setLoading(true);
        const result = await apiGet(`/kvis/schools/${schoolID}`);
        
        if (result.success) {
          setSchoolData(result.data);
          setError(null);
        } else {
          setError('Failed to load school data');
        }
      } catch (err) {
        console.error('Error fetching school data:', err);
        setError('Error loading school data');
      } finally {
        setLoading(false);
      }
    };

    fetchSchoolData();
  }, [schoolID]);

  const renderRow = (label: string, value: any, key?: string) => {
    const formattedValue = key ? formatCellValue(value, key) : (value || '-');
    
    return (
      <tr key={label}>
        <td style={{
          padding: '8px 12px',
          fontWeight: '600',
          color: '#555',
          fontSize: '13px',
          borderBottom: '1px solid #e5e7eb',
          background: '#f9fafb',
          width: '40%',
        }}>
          {label}
        </td>
        <td style={{
          padding: '8px 12px',
          color: '#333',
          fontSize: '13px',
          borderBottom: '1px solid #e5e7eb',
        }}>
          {formattedValue}
        </td>
      </tr>
    );
  };

  const pmShriDisplayValue = (() => {
    const raw =
      schoolData?.isPMShree ??
      basicInfo?.isPMShree ??
      schoolKPIData?.isPMShree ??
      schoolKPIData?.is_pm_shree ??
      schoolKPIData?.schoolDetails?.isPMShree ??
      schoolKPIData?.schoolDetails?.is_pm_shree;
    if (raw === null || raw === undefined || raw === '') return '-';
    return formatCellValue(raw, 'isPMShree');
  })();

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
    }} onClick={onClose}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        maxWidth: '900px',
        width: '90%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
      }} onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div style={{
          background: 'linear-gradient(90deg, #481789 0%, #6B1786 100%)',
          color: 'white',
          padding: '20px 24px',
          borderRadius: '12px 12px 0 0',
          position: 'relative',
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              fontWeight: 'bold',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            ×
          </button>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', paddingRight: '40px' }}>
            {schoolName}
          </h2>
          <p style={{ margin: '4px 0 12px 0', fontSize: '13px', opacity: 0.9 }}>
            School ID: {schoolID}
          </p>
          <button
            onClick={handleViewAll}
            style={{
              background: 'white',
              color: '#6B1786',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            📋 View All Details
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: '0',
          overflowY: 'auto',
          flex: 1,
        }}>
          {loading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px',
              color: '#666',
            }}>
              <KVSLoader size={60} color="#6B1786" />
              <span style={{ marginTop: '20px', fontSize: '14px' }}>Loading school data...</span>
            </div>
          ) : error ? (
            <div style={{
              padding: '40px 24px',
              textAlign: 'center',
              color: '#dc2626',
            }}>
              <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>⚠️ {error}</p>
              <p style={{ fontSize: '13px', color: '#666' }}>Please try again later</p>
            </div>
          ) : schoolData ? (
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
            }}>
              <tbody>
                {/* Basic Information */}
                <tr>
                  <td colSpan={2} style={{
                    padding: '16px 24px 12px',
                    background: '#6B1786',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '14px',
                    letterSpacing: '0.5px',
                  }}>
                    📋 BASIC INFORMATION
                  </td>
                </tr>
                {renderRow('School Name', schoolData.schoolName)}
                {renderRow('Region Office', schoolData.roName)}
                {renderRow('PM SHRI', pmShriDisplayValue)}
                {renderRow('Status', schoolData.status === 1 ? 'Active' : 'Inactive')}

                {/* Contact Information */}
                <tr>
                  <td colSpan={2} style={{
                    padding: '16px 24px 12px',
                    background: '#6B1786',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '14px',
                    letterSpacing: '0.5px',
                  }}>
                    📞 CONTACT INFORMATION
                  </td>
                </tr>
                {renderRow('School Contact', schoolData.contactNo)}
                {renderRow('School Email', schoolData.emailID)}
                {renderRow('Website', schoolData.website)}
                {renderRow('Principal Name', schoolData.principalName)}
                {renderRow('Principal Contact', schoolData.principalContactNo)}
                {schoolData.contactDetails && schoolData.contactDetails.length > 0 && (
                  <>
                    {schoolData.contactDetails.map((contact, index) => (
                      <React.Fragment key={contact.id}>
                        {index > 0 && (
                          <tr>
                            <td colSpan={2} style={{ padding: '4px 0', borderBottom: '1px dashed #e0e0e0' }}></td>
                          </tr>
                        )}
                        <tr>
                          <td colSpan={2} style={{
                            padding: '8px 24px',
                            background: '#f8f4fa',
                            fontWeight: '600',
                            fontSize: '13px',
                            color: '#6B1786',
                          }}>
                            {contact.roleName}
                          </td>
                        </tr>
                        {renderRow('Name', contact.name)}
                        {renderRow('Contact No', contact.contactNo)}
                        {renderRow('Email', contact.emailID)}
                      </React.Fragment>
                    ))}
                  </>
                )}

                {/* KPI-specific Data OR Location Information */}
                {selectedKPI && selectedKPI.category && selectedKPI.kpiField && schoolKPIData ? (
                  <>
                    <tr>
                      <td colSpan={2} style={{
                        padding: '16px 24px 12px',
                        background: '#6B1786',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '14px',
                        letterSpacing: '0.5px',
                      }}>
                        📊 {selectedKPI.category.toUpperCase()} - {selectedKPI.kpiLabel || selectedKPI.kpiField}
                      </td>
                    </tr>
                    {Object.entries(schoolKPIData).map(([key, value]) => {
                      // Skip internal/metadata fields, nested objects, and fields shown in other sections
                      const excludedFields = [
                        // IDs and metadata
                        'schoolID', 'school_id', 'id', 'kvID', 'uuid', 'createdAt', 'updatedAt', 'status',
                        // School basic info
                        'schoolName', 'schoolDetails',
                        // Location/RO info
                        'roID', 'roName', 'stateID', 'stateName', 'districtID', 'districtName', 'district',
                        'city', 'postalAddress1', 'postalAddress2', 'postalAddress3', 'pinCode',
                        'latitude', 'longitude', 'stationID', 'stationType', 'locationType', 'constituencyID',
                        // Contact info
                        'contactNo', 'emailID', 'website', 'principalName', 'principalContactNo', 'contactDetails',
                        // Infrastructure
                        'isPMShree', 'schoolBuilding', 'infraFacility',
                        // Academic info
                        'uptoClass', 'sections', 'establishmentYear', 'udiseCode', 'cbseAffliation', 'cbseSchoolCode',
                        // Sponsorship
                        'sponsoredBy', 'sponsoringAgency', 'sponsoringAgencyQuota'
                      ];
                      
                      if (excludedFields.includes(key) || typeof value === 'object') return null;
                      
                      // Format the label nicely
                      const label = key
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, str => str.toUpperCase())
                        .trim()
                        .replace(/^No /, 'No. of ');
                      
                      return renderRow(label, value, key);
                    })}
                  </>
                ) : (
                  <>
                    <tr>
                      <td colSpan={2} style={{
                        padding: '16px 24px 12px',
                        background: '#6B1786',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '14px',
                        letterSpacing: '0.5px',
                      }}>
                        📍 LOCATION DETAILS
                      </td>
                    </tr>
                    {renderRow('State', schoolData.stateName || schoolData.stateID)}
                    {renderRow('District', schoolData.districtName || schoolData.districtID)}
                    {renderRow('City', schoolData.city)}
                    {renderRow('Station', schoolData.stationName, 'stationName')}
                    {renderRow('Station Type', schoolData.stationType, 'stationType')}
                    {renderRow('Constituency', schoolData.constituencyName || schoolData.constituencyID)}
                    {renderRow('Address Line 1', schoolData.postalAddress1)}
                    {renderRow('Address Line 2', schoolData.postalAddress2)}
                    {renderRow('Address Line 3', schoolData.postalAddress3)}
                    {renderRow('PIN Code', schoolData.pinCode)}
                    {renderRow('Latitude', schoolData.latitude)}
                    {renderRow('Longitude', schoolData.longitude)}
                    {renderRow('Location Type', schoolData.locationType, 'locationType')}
                  </>
                )}

                {/* Academic Information */}
                <tr>
                  <td colSpan={2} style={{
                    padding: '16px 24px 12px',
                    background: '#6B1786',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '14px',
                    letterSpacing: '0.5px',
                  }}>
                    🎓 ESTABLISHMENT INFORMATION
                  </td>
                </tr>
                {renderRow('Establishment Year', schoolData.establishmentYear)}
                {schoolData.uptoClass && renderRow('Up to Class', schoolData.uptoClass, 'uptoClass')}
                {schoolData.sections && renderRow('Sections', schoolData.sections, 'sections')}
                {renderRow('UDISE Code', schoolData.udiseCode)}
                {renderRow('CBSE Affiliation', schoolData.cbseAffliation)}
                {renderRow('CBSE School Code', schoolData.cbseSchoolCode)}

                {/* Infrastructure
                <tr>
                  <td colSpan={2} style={{
                    padding: '16px 24px 12px',
                    background: '#6B1786',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '14px',
                    letterSpacing: '0.5px',
                  }}>
                    🏫 INFRASTRUCTURE
                  </td>
                </tr>
                {schoolData.schoolBuilding !== undefined && renderRow('School Building', schoolData.schoolBuilding, 'schoolBuilding')}
                {schoolData.infraFacility !== undefined && renderRow('Infrastructure Facility', schoolData.infraFacility, 'infraFacility')} */}

                {/* Sponsorship */}
                <tr>
                  <td colSpan={2} style={{
                    padding: '16px 24px 12px',
                    background: '#6B1786',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '14px',
                    letterSpacing: '0.5px',
                  }}>
                    💼 SPONSORSHIP
                  </td>
                </tr>
                {renderRow('Sponsored By', schoolData.sponsoredBy, 'sponsoredBy')}
                {renderRow('Sponsoring Agency', schoolData.sponsoringAgency)}
               
               
               
                {/* Ownership */}
                <tr>
                  <td colSpan={2} style={{
                    padding: '16px 24px 12px',
                    background: '#6B1786',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '14px',
                    letterSpacing: '0.5px',
                  }}>
                    💼 LAND STATUS
                  </td>
                </tr>
                {renderRow('Ownership Type', schoolData.ownershipType, 'ownershipType')}
    


                {/* Metadata */}
                {/* <tr>
                  <td colSpan={2} style={{
                    padding: '16px 24px 12px',
                    background: '#6B1786',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '14px',
                    letterSpacing: '0.5px',
                  }}>
                    🕐 METADATA
                  </td>
                </tr>
                {schoolData.createdAt && renderRow('Created At', new Date(schoolData.createdAt).toLocaleString())}
                {schoolData.updatedAt && renderRow('Updated At', new Date(schoolData.updatedAt).toLocaleString())} */}
              </tbody>
            </table>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default SchoolPopup;
